import { apiHandler } from "../buyerApiHandler";

/**
 * Tazty Wallet admin client.
 *
 * Talks straight to the Buyer API (REACT_APP_BUYER_BASE_URL) through
 * buyerApiHandler, which attaches x-api-key — the same path as the ₹99 Store
 * and Offer Management. Every credit/debit carries an idempotency key made
 * once per dialog, so a double click or a retry never moves money twice.
 */

export type WalletBucket = "refund" | "topup" | "promo" | "cashback";
export type WalletAdminBucket = "refund" | "promo";

export interface WalletBuckets {
  refund: number;
  topup: number;
  promo: number;
  cashback: number;
}

export interface WalletSummary {
  enabled: boolean;
  accounts: number;
  frozen: number;
  buckets: WalletBuckets;
  total: number;
}

export interface WalletListItem {
  user_id: number;
  name: string | null;
  phone_number: string | null;
  email: string | null;
  has_wallet: boolean;
  status: "active" | "frozen";
  balance: number;
  buckets: WalletBuckets;
  updated_at: string | null;
}

export interface WalletTransactionRow {
  id: string;
  txn_ref: string;
  direction: "credit" | "debit";
  amount: number;
  bucket: WalletBucket;
  type: string;
  status: string;
  title: string;
  note: string | null;
  order_id: number | null;
  order_number: string | null;
  balance_after: number | null;
  reason: string | null;
  created_by: string;
  created_at: string;
}

export interface WalletDetail {
  user: { id: number; name: string | null; phone_number: string | null; email: string | null };
  has_wallet: boolean;
  status: "active" | "frozen";
  frozen_reason: string | null;
  balance: number;
  buckets: WalletBuckets;
  transactions: WalletTransactionRow[];
  pagination: { page: number; limit: number; total: number; has_more: boolean };
}

export interface WalletAdjustPayload {
  amount: number;
  bucket: WalletAdminBucket;
  reason: string;
  customer_note?: string;
  idempotency_key: string;
  created_by?: string;
  notify?: boolean;
}

const BASE = "/admin/wallets";

/** The Buyer API wraps responses as { success, data }. */
const unwrap = <T,>(res: any): T => (res?.data ?? res) as T;

export const getWalletSummary = async (): Promise<WalletSummary> =>
  unwrap(await apiHandler("get", `${BASE}/summary`));

export const searchWallets = async (params: {
  search?: string;
  page?: number;
  limit?: number;
}): Promise<{ items: WalletListItem[]; pagination: { page: number; limit: number; has_more: boolean } }> =>
  unwrap(await apiHandler("get", BASE, undefined, { params }));

export const getWalletDetail = async (
  userId: number,
  params?: { page?: number; limit?: number },
): Promise<WalletDetail> =>
  unwrap(await apiHandler("get", `${BASE}/${userId}`, undefined, { params }));

export const adjustWallet = async (
  userId: number,
  direction: "credit" | "debit",
  payload: WalletAdjustPayload,
) =>
  unwrap(
    await apiHandler("post", `${BASE}/${userId}/${direction}`, payload, {
      headers: { "Content-Type": "application/json" },
    }),
  );

export const setWalletStatus = async (
  userId: number,
  status: "active" | "frozen",
  reason?: string,
): Promise<WalletDetail> =>
  unwrap(
    await apiHandler("patch", `${BASE}/${userId}/status`, { status, reason }, {
      headers: { "Content-Type": "application/json" },
    }),
  );

/** One key per opened dialog; reused on retry so the server dedupes it. */
export const newIdempotencyKey = (): string => {
  const c: any = (window as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/** Add-money settings: all admin-editable, applied immediately by the Buyer API. */
export interface TopupSettings {
  enabled: boolean;
  min: number;
  max: number;
  balance_cap: number;
  daily_cap: number;
  fee_bearer: "tazty" | "customer";
  fee_percent: number;
}

export interface TopupRow {
  id: number;
  amount: number;
  fee_amount: number;
  charged_amount: number | null;
  fee_bearer: "tazty" | "customer";
  status: "created" | "paid" | "credited" | "failed" | "expired";
  failure_reason: string | null;
  razorpay_order_id: string;
  razorpay_payment_id: string | null;
  created_at: string;
}

export const getTopupSettings = async (): Promise<TopupSettings> =>
  unwrap(await apiHandler("get", `${BASE}/topup-settings`));

export const saveTopupSettings = async (settings: TopupSettings): Promise<TopupSettings> =>
  unwrap(
    await apiHandler("put", `${BASE}/topup-settings`, settings, {
      headers: { "Content-Type": "application/json" },
    }),
  );

export const getUserTopups = async (userId: number): Promise<TopupRow[]> =>
  unwrap(await apiHandler("get", `${BASE}/${userId}/topups`));

// ── Wallet Audit Log (read-only) ──────────────────────────────────────────

export interface WalletAuditFilters {
  page?: number;
  limit?: number;
  /** IST dates, inclusive (YYYY-MM-DD). */
  from?: string;
  to?: string;
  /** Customer phone or name. */
  search?: string;
  type?: string;
  direction?: "credit" | "debit";
  status?: string;
  order_number?: string;
  created_by?: string;
}

export interface WalletAuditRow {
  id: string;
  txn_ref: string;
  created_at: string;
  user_id: number;
  customer_name: string | null;
  customer_phone: string | null;
  type: string;
  label: string;
  status: string;
  direction: "credit" | "debit";
  amount: number;
  bucket: string;
  balance_after: number | null;
  order_id: number | null;
  order_number: string | null;
  payment_ref: string | null;
  refund_id: number | null;
  reason: string | null;
  created_by: string;
  expires_at: string | null;
}

export interface WalletAuditSummary {
  money_added: number;
  spent_on_orders: number;
  on_hold: number;
  returned_unpaid: number;
  refunded_to_wallet: number;
  promo_and_cashback: number;
  expired: number;
  admin_credit: number;
  admin_debit: number;
  movements: number;
  customers: number;
  owed_now: number;
  wallets: number;
}

export interface AuditPage<T> {
  items: T[];
  pagination: { page: number; limit: number; total: number; has_more: boolean };
}

const auditParams = (f: WalletAuditFilters) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== ""));

export const getWalletAudit = async (f: WalletAuditFilters): Promise<AuditPage<WalletAuditRow>> =>
  unwrap(await apiHandler("get", `${BASE}/audit/transactions`, undefined, { params: auditParams(f) }));

export const getWalletAuditSummary = async (f: WalletAuditFilters): Promise<WalletAuditSummary> =>
  unwrap(await apiHandler("get", `${BASE}/audit/summary`, undefined, { params: auditParams(f) }));

export const getWalletAuditTopups = async (f: WalletAuditFilters): Promise<AuditPage<any>> =>
  unwrap(await apiHandler("get", `${BASE}/audit/topups`, undefined, { params: auditParams(f) }));

export const getWalletAuditOrders = async (f: WalletAuditFilters): Promise<AuditPage<any>> =>
  unwrap(await apiHandler("get", `${BASE}/audit/orders`, undefined, { params: auditParams(f) }));

export const exportWalletAudit = async (
  f: WalletAuditFilters,
): Promise<{ items: WalletAuditRow[]; truncated: boolean; max: number }> =>
  unwrap(await apiHandler("get", `${BASE}/audit/export`, undefined, { params: auditParams({ ...f, page: undefined, limit: undefined }) }));
